import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { getErrorMessage } from "utils/errorHelpers";
import type { BillingAccountDetailDto, PushBillingAccountToBillingSystemResultDto } from "client/api-v2";
import { liveQuoteTaxNotice } from "../liveQuoteTax";
import { pushBillingAccountToBillingSystem } from "../services/billingAccountService";

interface PushToZuoraDialogProps {
  open: boolean;
  billingAccount: BillingAccountDetailDto;
  onCancel: () => void;
  onPushed: (result: PushBillingAccountToBillingSystemResultDto) => void;
  /**
   * A refused push still opens and closes an attempt, so the account behind the dialog is stale
   * the moment it fails — the dialog stays open to report it, and this is what reloads the page
   * under it.
   */
  onFailed: () => void;
}

interface PushFormValues {
  reason: string;
}

const formId = "push-to-zuora-form";
const defaultValues: PushFormValues = { reason: "" };
const maxReasonLength = 500;

// The reason is kept on the account's activity log for good, where a customer's details have no
// business being — so the field says so rather than leaving it to judgement.
const reasonGuidance = "Do not enter Personally Identifiable Information (PII) in the reason.";

function Monospace({ children }: { children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ fontFamily: "monospace" }}>
      {children}
    </Box>
  );
}

export function PushToZuoraDialog({ open, billingAccount, onCancel, onPushed, onFailed }: PushToZuoraDialogProps) {
  const [serverError, setServerError] = useState<string | undefined>();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<PushFormValues>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setServerError(undefined);
    }
  }, [open, reset]);

  const accountName = billingAccount.commonName ?? billingAccount.legalName;
  const reason = watch("reason");
  const liveOrderCount = billingAccount.liveOrderCount ?? 0;

  const onSubmit = async (values: PushFormValues) => {
    setServerError(undefined);
    try {
      onPushed(await pushBillingAccountToBillingSystem(billingAccount.code, values.reason.trim()));
    } catch (e) {
      setServerError(getErrorMessage(e));
      onFailed();
    }
  };

  const cancel = () => !isSubmitting && onCancel();

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Push to Zuora
        <IconButton
          aria-label="close"
          onClick={cancel}
          disabled={isSubmitting}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            {serverError && (
              <Grid size={12}>
                <Alert severity="error">Push failed: {serverError}</Alert>
              </Grid>
            )}

            {liveOrderCount > 0 && (
              <Grid size={12}>
                <Alert severity="info">{liveQuoteTaxNotice("Pushing", liveOrderCount)}</Alert>
              </Grid>
            )}

            <Grid size={12}>
              <Typography variant="body1">
                Push <strong>{accountName}</strong>&apos;s details (name, attention, address, tax exemption){" "}
                {billingAccount.zuoraAccountNumber ? (
                  <>
                    to its Zuora account <Monospace>{billingAccount.zuoraAccountNumber}</Monospace>?
                  </>
                ) : (
                  <>
                    to Zuora? No Zuora account is linked yet, so one is created under{" "}
                    <Monospace>{billingAccount.code}</Monospace>.
                  </>
                )}
              </Typography>
            </Grid>

            <Grid size={12}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", mb: 1 }}>
                <WarningAmberOutlinedIcon fontSize="small" color="warning" />
                <Typography variant="caption" color="text.secondary">
                  {reasonGuidance}
                </Typography>
              </Stack>
              <Controller
                name="reason"
                control={control}
                rules={{
                  validate: (value) => value.trim() !== "" || "Reason is required",
                  maxLength: {
                    value: maxReasonLength,
                    message: `Reason must be ${maxReasonLength} characters or fewer`,
                  },
                }}
                render={({ field: { ref, ...rest }, fieldState }) => (
                  <TextField
                    {...rest}
                    inputRef={ref}
                    autoFocus
                    required
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason"
                    placeholder="e.g. Zuora is out of date after an address change"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !reason.trim()}
        >
          {isSubmitting ? "Pushing..." : "Push to Zuora"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
